import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Search20Regular, Dismiss20Regular } from '@fluentui/react-icons';

interface MachineSearchProps {
    onMachineSelect: (machineId: string) => void;
    availableMachines: string[];
    getSuggestions?: (searchTerm: string) => string[];
}

export interface MachineSearchRef {
    focus: () => void;
    clear: () => void;
}

export const MachineSearch = forwardRef<MachineSearchRef, MachineSearchProps>(({
    onMachineSelect,
    availableMachines,
    getSuggestions
}, ref) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);


    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        },
        clear: () => {
            setSearchTerm('');
            setSelectedIndex(-1);
            setIsDropdownVisible(false);
        }
    }));


    const filteredMachines = getSuggestions
        ? getSuggestions(searchTerm).slice(0, 4)
        : availableMachines.filter((machine: string) =>
            machine.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 4);

    useEffect(() => {
        setIsDropdownVisible(searchTerm.length > 0 && filteredMachines.length > 0);
        setSelectedIndex(-1);
    }, [searchTerm, filteredMachines]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isDropdownVisible) {
            if (e.key === 'Enter') {
                const exactMatch = availableMachines.find(machine =>
                    machine.toLowerCase() === searchTerm.toLowerCase()
                );
                if (exactMatch) {

                    onMachineSelect(exactMatch);
                    handleClear();
                }
            } else if (e.key === 'Escape') {
                handleClear();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < filteredMachines.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredMachines.length) {
                    const selectedMachine = filteredMachines[selectedIndex];
                    onMachineSelect(selectedMachine);
                    handleClear();
                } else if (filteredMachines.length > 0) {
                    onMachineSelect(filteredMachines[0]);
                    handleClear();
                }
                break;
            case 'Escape':
                handleClear();
                break;
        }
    };


    const handleItemClick = (machine: string) => {
        onMachineSelect(machine);
        handleClear();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleClear = () => {
        setSearchTerm('');
        setSelectedIndex(-1);
        setIsDropdownVisible(false);
    };

    return (
        <>
            <style>
                {`
                    .placeholder-white::placeholder {
                        color: rgba(255, 255, 255, 0.7);
                    }
                    .placeholder-white::-webkit-input-placeholder {
                        color: rgba(255, 255, 255, 0.7);
                    }
                    .placeholder-white::-moz-placeholder {
                        color: rgba(255, 255, 255, 0.7);
                    }
                    .placeholder-white:-ms-input-placeholder {
                        color: rgba(255, 255, 255, 0.7);
                    }
                `}
            </style>
            <div
                data-tour="machine-search"
                title='CMD+F / CTRL+F'
                style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    zIndex: 1000,
                    fontFamily: 'Segoe UI, sans-serif'
                }}>
                <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    minWidth: '300px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(10px)'
                }}>
                    <Search20Regular style={{
                        color: 'white',
                        marginRight: '8px',
                        flexShrink: 0
                    }} />
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Search machines by ID..."
                        style={{
                            border: 'none',
                            outline: 'none',
                            flex: 1,
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            backgroundColor: 'transparent',
                            color: 'white'
                        }}
                        className="placeholder-white"
                    />
                    <button
                        onClick={handleClear}
                        style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '8px',
                            borderRadius: '2px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        <Dismiss20Regular style={{ color: 'white' }} />
                    </button>
                </div>

                {/* Dropdown with suggestions */}
                {isDropdownVisible && (
                    <div
                        ref={dropdownRef}
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: '0',
                            right: '0',
                            backgroundColor: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderTop: 'none',
                            borderRadius: '0 0 4px 4px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            maxHeight: `${4 * 40}px`, // Height of 4 search bars
                            overflowY: 'auto',
                            zIndex: 1001,
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        {filteredMachines.map((machine, index) => (
                            <div
                                key={machine}
                                onClick={() => handleItemClick(machine)}
                                style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    borderBottom: index < filteredMachines.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                    backgroundColor: index === selectedIndex ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                                    transition: 'background-color 0.1s ease',
                                    color: 'white'
                                }}
                                onMouseEnter={(e) => {
                                    if (index !== selectedIndex) {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (index !== selectedIndex) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                <Search20Regular style={{
                                    color: 'white',
                                    marginRight: '8px',
                                    verticalAlign: 'middle'
                                }} />
                                {machine}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
});

MachineSearch.displayName = 'MachineSearch';
